<script lang="ts">
  import { onMount } from 'svelte';

  let canvas: HTMLCanvasElement;

  onMount(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0, h = 0;
    let raf = 0;
    let paused = false;

    type Particle = { x: number; y: number; r: number; s: number; c: string; a: number; t: number };
    let ps: Particle[] = [];
    const cols = ['86,202,190', '217,165,54', '200,68,59'];

    const mk = (): Particle => ({
      x: Math.random() * w,
      y: h + Math.random() * h,
      r: Math.random() * 1.5 + 0.4,
      s: Math.random() * 0.28 + 0.06,
      c: cols[(Math.random() * cols.length) | 0],
      a: Math.random() * 0.5 + 0.12,
      t: Math.random() * 6,
    });

    const rs = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      ps = Array.from({ length: Math.min(46, Math.floor(w / 30)) }, mk);
    };

    const tick = () => {
      // Race guard: if a rAF was queued before `paused` was set, bail without
      // rescheduling.
      if (paused) return;
      ctx.clearRect(0, 0, w, h);
      for (const p of ps) {
        p.y -= p.s;
        p.t += 0.01;
        p.x += Math.sin(p.t) * 0.18;
        if (p.y < -10) {
          Object.assign(p, mk());
          p.y = h + 8;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, 7);
        ctx.fillStyle = `rgba(${p.c},${p.a})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };

    rs();
    tick();
    window.addEventListener('resize', rs);

    // Pause the rAF loop when the tab is hidden OR the hero is scrolled out
    // of view. Embers only sit behind the hero; running the loop after the
    // user has moved past it is wasted battery / CPU.
    let tabVisible = !document.hidden;
    let heroVisible = true;

    function recompute() {
      const want = tabVisible && heroVisible;
      if (want && paused) {
        paused = false;
        raf = requestAnimationFrame(tick);
      } else if (!want && !paused) {
        paused = true;
        cancelAnimationFrame(raf);
        raf = 0;
      }
    }

    const onVis = () => {
      tabVisible = !document.hidden;
      recompute();
    };
    document.addEventListener('visibilitychange', onVis);

    const hero = document.getElementById('top');
    let heroIO: IntersectionObserver | null = null;
    if (hero && 'IntersectionObserver' in window) {
      heroIO = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            heroVisible = e.isIntersecting;
            recompute();
          }
        },
        { threshold: 0 },
      );
      heroIO.observe(hero);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', rs);
      document.removeEventListener('visibilitychange', onVis);
      heroIO?.disconnect();
    };
  });
</script>

<canvas id="embers" bind:this={canvas}></canvas>
